import { motion } from "framer-motion";
import { Check, Package, CalendarClock, PackageCheck, Replace } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useStoreSettings } from "../context/StoreSettingsContext";
import { BoxComposer } from "./BoxComposer";
import api from "../lib/api";

const perks = [
  "Curato sulle tue abitudini di consumo",
  "Consegna ogni mese, alla stessa data",
  "Sospendi, salta o modifica in un tap",
  "Spedizione sempre inclusa",
];

const rules = [
  {
    icon: CalendarClock,
    title: "Componi il box ogni mese",
    body: "Ogni mese scegli tu cosa mettere nel box, ma solo tra i prodotti effettivamente disponibili nel momento in cui effettui l'ordine.",
  },
  {
    icon: PackageCheck,
    title: "Non lo dimentichi mai",
    body: "Se salti la selezione per uno o due mesi, il box parte comunque: lo compone il nostro team basandosi sui tuoi ordini precedenti, così ricevi sempre un pacco adeguato.",
  },
  {
    icon: Replace,
    title: "Sostituzioni libere",
    body: "Se un prodotto è esaurito puoi sostituirlo con qualsiasi altro, anche di categoria diversa, purché il valore totale non superi i 92€. Il prezzo resta sempre fisso: 79€ al mese.",
  },
];

const euro = (n) => `€${n.toFixed(2).replace(".", ",")}`;

export const SubscriptionBox = ({ boxProducts }) => {
  const { user } = useAuth();
  const { canShop } = useStoreSettings();
  const navigate = useNavigate();
  const [composerOpen, setComposerOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const total = boxProducts.reduce((s, p) => s + p.price, 0);

  const activateBox = () => {
    if (!user) {
      toast("Accedi per attivare il tuo Box Mensile");
      navigate("/login");
      return;
    }
    setComposerOpen(true);
  };

  const payBox = async (items, frequency = "monthly") => {
    if (paying) return;
    if (!user?.address?.via || !user?.address?.cap || !user?.address?.telefono) {
      toast.error("Completa l'indirizzo di spedizione nel tuo account prima di attivare il Box");
      setComposerOpen(false);
      navigate("/account");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items,
        plan: "box_mensile",
        frequency,
        email: user.email,
        customer_name: user.name,
        address: user.address,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attivazione non riuscita");
      setPaying(false);
    }
  };

  return (
    <section id="box-mensile" className="relative overflow-hidden bg-panel py-24 text-ink md:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-2 md:px-10">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-5 flex items-center gap-2 text-xs uppercase tracking-overline text-sage">
            <Package className="h-4 w-4" strokeWidth={1.6} /> L'abbonamento
          </p>
          <h2 className="font-serif text-5xl font-light leading-[0.95] md:text-7xl" data-testid="box-heading">
            Il Box <span className="italic text-sage">Mensile</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ink/70 md:text-lg">
            Una selezione dei tuoi essenziali ricorrenti, consegnata ogni mese — o ogni due, se preferisci — senza pensieri.
            Prezzo fisso: <span className="text-ink">79€ a box, spedizione inclusa</span> — riempilo fino a 92€ di prodotti. Più riempi, più risparmi.
          </p>

          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-ink/85">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-electric/20 text-electric">
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                </span>
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            {canShop ? (
              <button
                onClick={activateBox}
                data-testid="activate-box-button"
                className="rounded-full bg-sage px-8 py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.04] hover:bg-sage-deep active:scale-95"
              >
                Scegli i prodotti e attiva il box
                <span className="block text-xs font-normal opacity-80">79€ a box · fino a 92€ di prodotti · ogni mese o ogni 2 mesi</span>
              </button>
            ) : (
              <div className="rounded-2xl border border-dashed border-sage/40 bg-sage/10 px-6 py-4" data-testid="box-coming-soon">
                <p className="text-sm font-semibold text-ink">Il Box Mensile sarà attivabile all'apertura del negozio</p>
                <p className="mt-0.5 text-xs text-ink-soft">Intanto puoi scoprire come funziona e preparare la tua lista.</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-2 gap-4"
          data-testid="box-preview"
        >
          {boxProducts.slice(0, 6).map((p, i) => (
            <div
              key={p.id}
              className={`clip-frame rounded-2xl bg-white/5 ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-square"}`}
            >
              <img src={p.image} alt={p.name} className="h-full w-full object-contain bg-white p-2" />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Come funziona — le regole del box */}
      <div className="mx-auto mt-16 max-w-7xl px-6 md:mt-20 md:px-10">
        <p className="mb-6 text-xs uppercase tracking-overline text-electric">Come funziona il tuo box</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {rules.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
              className="rounded-2xl border border-ink/15 bg-cream/[0.04] p-7 md:p-8"
              data-testid={`box-rule-${i}`}
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-electric/20 text-electric">
                <r.icon className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <h3 className="mt-5 font-serif text-2xl text-ink">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/70">{r.body}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {composerOpen && (
        <BoxComposer activation onClose={() => setComposerOpen(false)} onActivate={payBox} />
      )}
    </section>
  );
};
